import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BadgePercent, CheckCircle2, Loader2, Pencil, Plus, ShieldCheck,
  Trophy, Users, PlusCircle, MinusCircle, Activity,
  Archive, ListChecks,
} from "lucide-react";
import AdminLayout from "../components/layout/AdminLayout";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import InputField from "../components/ui/InputField";
import SelectField from "../components/ui/SelectField";
import LoaderSpinner from "../components/ui/LoaderSpinner";
import { useBdTargetBonusStore, type BdAssignment, type BdBonusTier, type BdTarget, type TargetMetric, type TargetPeriod } from "../store/useBdTargetBonusStore";
import toast from "react-hot-toast";

// ── helpers ──────────────────────────────────────────────────────────────────
const baseTier = (): BdBonusTier => ({ tierName: "Tier 1", revenueMilestone: 15000, bonusType: "Fixed Amount", bonusValue: 250, notes: "", status: "Active" });
const defaultTiers = () => [
  baseTier(),
  { ...baseTier(), tierName: "Tier 2", revenueMilestone: 20000, bonusValue: 500 },
  { ...baseTier(), tierName: "Tier 3", revenueMilestone: 30000, bonusValue: 1000 },
];
const defaultAssignmentForm = () => ({ targetId: "", bonusPlanId: "" });
// The four things a BD can be targeted on, and how each one is entered.
// `unit` decides whether the field is money or a plain count — a connects
// target of "$300" would read as nonsense.
const TARGET_METRICS = [
  { value: "revenue", label: "Revenue", unit: "currency", hint: "Value of projects created in the period" },
  { value: "bids", label: "Bids", unit: "count", hint: "Bids submitted in the period" },
  { value: "connects", label: "Connects", unit: "count", hint: "Connects consumed by bids in the period" },
  { value: "proposals", label: "Proposals", unit: "count", hint: "Proposals created in the period" },
] as const;

const TARGET_PERIODS = [
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly (Mon–Sun)" },
] as const;

// The period containing today, as dates. Filled in automatically so nobody has
// to work out which Monday this week started on — and so the dates and the
// chosen period cannot disagree.
const periodDates = (period: string, from = new Date()) => {
  if (period === "weekly") {
    const start = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
    const day = start.getUTCDay();
    start.setUTCDate(start.getUTCDate() - (day === 0 ? 6 : day - 1));
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
  }
  const start = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1));
  const end = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 0));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
};

const defaultTargetForm = () => ({
  businessDeveloper: "",
  metric: "revenue" as TargetMetric,
  period: "monthly" as TargetPeriod,
  // Recurring by default: a target nobody renews stops measuring anything the
  // moment the period ends, which is how the old monthly-only targets went
  // stale between cycles.
  recurring: true,
  targetValue: 15000,
  targetMonth: new Date().getMonth() + 1,
  targetYear: new Date().getFullYear(),
  revenueTarget: 15000,
  effectiveFrom: periodDates("monthly").from,
  effectiveTo: periodDates("monthly").to,
  notes: "",
  bonusPlanId: "", // bonus plan chosen in the same modal (no separate assign step)
});

const money = (n: number) => `$${Math.round(n || 0).toLocaleString()}`;
const getName = (v: any): string => !v ? "—" : typeof v === "string" ? v : v?.fullName || v?.email || "—";
const getId = (v: any): string => !v ? "" : typeof v === "string" ? v : v?._id || "";

// ── tiny UI components ────────────────────────────────────────────────────────
function Panel({ title, subtitle, icon, children, action }: {
  title: string; subtitle?: string; icon?: ReactNode; children: ReactNode; action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {icon}
          <div className="min-w-0">
            <h2 className="font-extrabold text-slate-800 text-sm">{title}</h2>
            {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-dashed border-slate-200 py-8 px-4 text-center text-xs text-slate-400 font-medium">
      {text}
    </div>
  );
}

function FormRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-bold text-slate-600">{label}</label>
      {children}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────
export default function AdminTargetBonusPage() {
  const {
    loading, allTargetsLoading,
    overview, allTargets, developers,
    historicalRecords, activityLogs,
    fetchOverview, fetchAllTargets, fetchDevelopers, fetchWorkspace, fetchHistorical, fetchActivityLogs,
    createTarget, updateTarget, archiveTarget,
    createPlan, updatePlan,
    createAssignment, updateAssignment,
  } = useBdTargetBonusStore();

  const [modalOpen, setModalOpen] = useState<"target" | "plan" | "assign" | null>(null);
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);

  const [savingTarget, setSavingTarget] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const [targetForm, setTargetForm] = useState(defaultTargetForm());
  const [planName, setPlanName] = useState("");
  const [tiers, setTiers] = useState<BdBonusTier[]>(defaultTiers());
  const [assignmentForm, setAssignmentForm] = useState(defaultAssignmentForm());
  const [assignmentConflict, setAssignmentConflict] = useState<{ open: boolean; assignment: BdAssignment | null; message: string }>({ open: false, assignment: null, message: "" });

  useEffect(() => {
    fetchOverview();
    fetchAllTargets();
    fetchDevelopers();
    fetchWorkspace();
    fetchHistorical();
    fetchActivityLogs();
  }, [fetchOverview, fetchAllTargets, fetchDevelopers, fetchWorkspace, fetchHistorical, fetchActivityLogs]);

  const targets = overview?.targets || [];
  const plans = overview?.plans || [];
  const assignments = overview?.assignments || [];

  useEffect(() => {
    if (modalOpen === "assign" && !editingAssignmentId && !assignmentForm.bonusPlanId && plans.length > 0) {
      const firstPlanId = plans[0]?._id;
      if (firstPlanId) {
        setAssignmentForm((f) => ({ ...f, bonusPlanId: firstPlanId }));
      }
    }
  }, [modalOpen, editingAssignmentId, assignmentForm.bonusPlanId, plans]);

  const assignedTargetIds = useMemo(() => new Set(assignments.map((a) => getId(a.target))), [assignments]);

  const resetTargetEditor = () => { setEditingTargetId(null); setTargetForm(defaultTargetForm()); setModalOpen(null); };
  const resetPlanEditor = () => { setEditingPlanId(null); setPlanName(""); setTiers(defaultTiers()); setModalOpen(null); };
  const resetAssignmentEditor = () => { setEditingAssignmentId(null); setAssignmentForm(defaultAssignmentForm()); setModalOpen(null); };

  const openTargetForEdit = (t: BdTarget) => {
    setEditingTargetId(t._id);
    const metric = t.metric || "revenue";
    setTargetForm({
      ...defaultTargetForm(),
      businessDeveloper: getId(t.businessDeveloper),
      metric,
      period: t.period || "monthly",
      // A spawned period is a cycle of a recurring target, so it reads as
      // recurring here even though the flag lives on its parent.
      recurring: Boolean(t.recurring || t.spawnedFrom),
      targetValue: t.targetValue ?? t.revenueTarget ?? 0,
      targetMonth: t.targetMonth,
      targetYear: t.targetYear,
      revenueTarget: t.revenueTarget,
      effectiveFrom: t.effectiveFrom?.slice(0, 10) ?? "",
      effectiveTo: t.effectiveTo?.slice(0, 10) ?? "",
      notes: t.notes || "",
      bonusPlanId: "",
    });
    setModalOpen("target");
  };

  const openAssignmentForEdit = (a: BdAssignment) => {
    setEditingAssignmentId(a._id);
    setAssignmentForm({ targetId: getId(a.target), bonusPlanId: a.bonusPlan ? getId(a.bonusPlan) : "" });
    setModalOpen("assign");
  };

  const openPlanForEdit = (plan: any) => {
    setEditingPlanId(plan._id);
    setPlanName(plan.planName);
    setTiers(plan.tiers);
    setModalOpen("plan");
  };

  const handleTargetSubmit = async () => {
    if (!targetForm.businessDeveloper) { toast.error("Please select a Business Developer"); return; }
    setSavingTarget(true);
    try {
      const body = {
        ...targetForm,
        targetValue: Number(targetForm.targetValue),
        // Only meaningful for revenue; the server ignores it for the others.
        revenueTarget: Number(targetForm.targetValue),
        effectiveFrom: new Date(targetForm.effectiveFrom).toISOString(),
        effectiveTo: new Date(targetForm.effectiveTo).toISOString(),
      };
      const ok = editingTargetId ? await updateTarget(editingTargetId, body) : await createTarget(body);
      if (ok) resetTargetEditor();
    } finally { setSavingTarget(false); }
  };

  const handleArchiveTarget = async (id: string) => {
    setArchivingId(id);
    try { await archiveTarget(id); } finally { setArchivingId(null); }
  };

  const handlePlanSubmit = async () => {
    if (!planName.trim()) { toast.error("Please enter a plan name"); return; }
    setSavingPlan(true);
    try {
      const ok = editingPlanId ? await updatePlan(editingPlanId, { planName, tiers, notes: "" }) : await createPlan({ planName, tiers, notes: "" });
      if (ok) resetPlanEditor();
    } finally { setSavingPlan(false); }
  };

  const handleAssignmentSubmit = async () => {
    if (!assignmentForm.targetId) { toast.error("Please select a target"); return; }
    const selectedTarget = allTargets.find((t) => t._id === assignmentForm.targetId);
    const businessDeveloper = getId(selectedTarget?.businessDeveloper);
    if (!businessDeveloper) { toast.error("Could not determine Business Developer from selected target"); return; }
    setSavingAssignment(true);
    try {
      const payload = {
        businessDeveloper,
        targetId: assignmentForm.targetId,
        ...(assignmentForm.bonusPlanId && { bonusPlanId: assignmentForm.bonusPlanId })
      };
      const result = editingAssignmentId ? await updateAssignment(editingAssignmentId, payload) : await createAssignment(payload);
      if (result.ok) { resetAssignmentEditor(); return; }
      if (result.conflict) setAssignmentConflict({ open: true, assignment: result.conflict, message: result.message || "Active assignment already exists." });
    } finally { setSavingAssignment(false); }
  };

  const addTier = () => {
    const prev = tiers[tiers.length - 1];
    setTiers([...tiers, { tierName: `Tier ${tiers.length + 1}`, revenueMilestone: (prev?.revenueMilestone || 10000) + 5000, bonusType: "Fixed Amount", bonusValue: (prev?.bonusValue || 100) + 250, notes: "", status: "Active" }]);
  };

  const removeTier = (i: number) => {
    if (tiers.length <= 1) { toast.error("At least one tier is required."); return; }
    setTiers(tiers.filter((_, idx) => idx !== i));
  };

  if (loading && !overview) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
          <LoaderSpinner size={44} />
          <p className="text-slate-500 font-medium animate-pulse">Loading target & bonus system...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-8">

        {/* Header */}
        <div className="pb-4 border-b border-slate-100">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-bold tracking-wider uppercase mb-2">
            <ShieldCheck size={11} /> Management Console
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="text-xl font-extrabold tracking-tight text-[#0b1c30]">BD Targets & Bonuses</h1>
          </div>
          <p className="mt-1 text-xs text-slate-400">Track business developers' performance against configured monthly targets and dynamic bonus commission tiers.</p>
        </div>

        {/* Loading bar */}
        {loading && (
          <div className="flex items-center gap-3 text-xs text-blue-600 bg-blue-50/50 border border-blue-100 rounded-xl px-4 py-2 w-max">
            <Loader2 size={14} className="animate-spin" /> Syncing targets with active Upwork performance logs...
          </div>
        )}

        {/* Stats hidden for now.
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard icon={Target} label="Active Targets" value={String(allTargets.filter((t) => t.status === "Active").length)} tone="blue" />
          <StatCard icon={BadgePercent} label="Bonus Plans" value={String(plans.length)} tone="green" />
          <StatCard icon={Users} label="Assignments" value={String(assignments.length)} tone="indigo" />
          <StatCard icon={History} label="History" value={String(historicalRecords.length)} tone="slate" />
        </div>
        */}

        {/* Section 1 — Active Targets & Live Progress (hidden for now)
        <Panel
          title="Active Targets & Live Progress"
          subtitle="Live revenue progress of assigned BDs against their benchmarks"
          icon={<Trophy size={20} className="text-amber-500" />}
        >
          {assignments.length === 0 ? (
            <EmptyState text="No active BD target assignments found for the current cycle." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["BD Name", "Period", "Target", "Achieved", "Progress", "Status", "Bonus Plan", "Actions"].map((h) => (
                      <th key={h} className="text-left pb-3 pr-4 last:pr-0 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {assignments.map((a) => {
                    const pct = a.achievementPercentage || 0;
                    const targetVal = a.target?.revenueTarget || 0;
                    const achievedVal = a.revenueAchieved || 0;
                    const barColor = pct >= 100 ? "bg-emerald-500" : pct >= 75 ? "bg-blue-500" : pct >= 40 ? "bg-indigo-500" : "bg-amber-500";
                    const statusStyle = pct >= 100 ? "bg-emerald-50 text-emerald-700 border-emerald-100" : pct >= 50 ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-amber-50 text-amber-700 border-amber-100";
                    const statusLabel = pct >= 100 ? "Achieved" : pct >= 80 ? "On Track" : pct >= 40 ? "Behind" : "At Risk";
                    return (
                      <tr key={a._id} className="hover:bg-slate-50/60 transition">
                        <td className="py-3 pr-4 font-bold text-slate-800 whitespace-nowrap">{getName(a.businessDeveloper)}</td>
                        <td className="py-3 pr-4 font-bold text-slate-700 whitespace-nowrap">{a.target?.targetMonth}/{a.target?.targetYear}</td>
                        <td className="py-3 pr-4 font-black text-slate-800 whitespace-nowrap">{money(targetVal)}</td>
                        <td className="py-3 pr-4 font-bold text-slate-700 whitespace-nowrap">{money(achievedVal)}</td>
                        <td className="py-3 pr-4 min-w-[120px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <span className="text-[10px] font-black text-slate-600 shrink-0">{pct}%</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusStyle}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">
                          {a.bonusPlan ? a.bonusPlan.planName : <span className="text-slate-300 italic">No plan</span>}
                        </td>
                        <td className="py-3">
                          <button
                            onClick={() => openAssignmentForEdit(a)}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2462b2] hover:text-[#1e52a0] border border-slate-200 hover:border-blue-200 px-2.5 py-1 rounded-lg transition"
                          >
                            <Pencil size={11} /> Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        */}

        {/* Section 2 — Existing Targets */}
        <Panel
          title="Existing Targets"
          subtitle="All target records — edit, assign, or archive"
          icon={<ListChecks size={20} className="text-[#2462b2]" />}
          action={
            <div className="flex items-center gap-2">
              <button
                onClick={() => { resetTargetEditor(); setModalOpen("target"); }}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#2462b2] hover:bg-[#1e52a0] px-2.5 py-1.5 rounded-lg transition"
              >
                <Plus size={11} /> New Target
              </button>
            </div>
          }
        >
          {allTargetsLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-6">
              <Loader2 size={14} className="animate-spin" /> Loading targets...
            </div>
          ) : allTargets.length === 0 ? (
            <EmptyState text="No target records found." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["BD Name", "Period", "Target", "Effective Dates", "Status", "Assigned", "Actions"].map((h) => (
                      <th key={h} className="text-left pb-3 pr-4 last:pr-0 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {allTargets.map((t) => {
                    const isAssigned = assignedTargetIds.has(t._id);
                    const isArchived = t.status === "Archived";
                    return (
                      <tr key={t._id} className="hover:bg-slate-50/60 transition">
                        <td className="py-3 pr-4 font-bold text-slate-800 whitespace-nowrap">{getName(t.businessDeveloper)}</td>
                        {/* Which cycle, and whether it carries on by itself.
                            Four metrics now share this table, so a row that
                            only showed a number would be unreadable. */}
                        <td className="py-3 pr-4 font-bold text-slate-700 whitespace-nowrap">
                          {t.periodKey || `${t.targetMonth}/${t.targetYear}`}
                          <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            {t.period === "weekly" ? "Weekly" : "Monthly"}
                            {(t.recurring || t.spawnedFrom) && " · repeats"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 font-black text-slate-800 whitespace-nowrap">
                          {(() => {
                            const metric = t.metric || "revenue";
                            const value = t.targetValue ?? t.revenueTarget;
                            const meta = TARGET_METRICS.find((m) => m.value === metric);
                            return (
                              <>
                                {meta?.unit === "currency" ? money(value) : Number(value || 0).toLocaleString()}
                                <span className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                                  {meta?.label || metric}
                                </span>
                              </>
                            );
                          })()}
                        </td>
                        <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">
                          {t.effectiveFrom ? new Date(t.effectiveFrom).toLocaleDateString() : "—"} → {t.effectiveTo ? new Date(t.effectiveTo).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${isArchived ? "bg-slate-50 text-slate-400 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-100"}`}>
                            {isArchived ? "Archived" : "Active"}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${isAssigned ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-amber-50 text-amber-600 border-amber-100"}`}>
                            {isAssigned ? "Assigned" : "Unassigned"}
                          </span>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            {!isArchived ? (
                              <>
                                <button
                                  onClick={() => openTargetForEdit(t)}
                                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2462b2] hover:text-[#1e52a0] border border-slate-200 hover:border-blue-200 px-2.5 py-1.5 rounded-lg transition"
                                >
                                  <Pencil size={11} /> Edit
                                </button>
                                <button
                                  onClick={() => handleArchiveTarget(t._id)}
                                  disabled={archivingId === t._id}
                                  className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-200 px-2.5 py-1.5 rounded-lg transition disabled:opacity-50"
                                >
                                  {archivingId === t._id ? <Loader2 size={11} className="animate-spin" /> : <Archive size={11} />}
                                  Archive
                                </button>
                              </>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        {/* Section 3 — Existing Bonus Plans */}
        <Panel
          title="Existing Bonus Plans"
          subtitle="Commission configurations available"
          icon={<BadgePercent size={20} className="text-emerald-500" />}
          action={
            <button
              onClick={() => { resetPlanEditor(); setModalOpen("plan"); }}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1.5 rounded-lg transition"
            >
              <Plus size={11} /> New Bonus Plan
            </button>
          }
        >
          {plans.length === 0 ? (
            <EmptyState text="No bonus plans configured." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Plan Name", "Tiers", "Milestone Breakdown", "Actions"].map((h) => (
                      <th key={h} className="text-left pb-3 pr-4 last:pr-0 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {plans.map((plan) => (
                    <tr key={plan._id} className="hover:bg-slate-50/60 transition">
                      <td className="py-3 pr-4 font-bold text-slate-800 whitespace-nowrap">{plan.planName}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-50 text-emerald-700 border-emerald-100">
                          {plan.tiers.length} tier{plan.tiers.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex gap-2 flex-wrap">
                          {plan.tiers.map((t, idx) => (
                            <span key={idx} className="shrink-0 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1 text-[10px] text-slate-600 whitespace-nowrap">
                              <span className="font-bold text-slate-400">{t.tierName}:</span>{" "}
                              {money(t.revenueMilestone)} →{" "}
                              <span className="text-emerald-600 font-bold">+{t.bonusType === "Fixed Amount" ? money(t.bonusValue) : `${t.bonusValue}%`}</span>
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => openPlanForEdit(plan)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#2462b2] hover:text-[#1e52a0] border border-slate-200 hover:border-blue-200 px-2.5 py-1.5 rounded-lg transition"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {/* ── MODALS ── */}

      {/* Target Modal (create + edit) */}
      <Modal isOpen={modalOpen === "target"} onClose={resetTargetEditor} title={editingTargetId
? "Edit Target" : "Configure Target Benchmark"}>
        <div className="space-y-4">
          <SelectField
            label="Business Developer"
            value={targetForm.businessDeveloper}
            onChange={(e) => setTargetForm({ ...targetForm, businessDeveloper: e.target.value })}
            placeholder="Select a BD employee"
            options={developers.map((bd) => ({
              value: bd._id,
              label: bd.fullName || bd.email,
            }))}
            required
          />
          {/* What is being targeted, and over what cycle. Changing either one
              refills the dates, so the window always matches the period. */}
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Metric"
              value={targetForm.metric}
              onChange={(e) => {
                const metric = e.target.value;
                setTargetForm({
                  ...targetForm,
                  metric: metric as TargetMetric,
                  // Sensible starting numbers per metric — $15,000 of connects
                  // would be a strange default.
                  targetValue: metric === "revenue" ? 15000 : metric === "connects" ? 300 : metric === "bids" ? 40 : 10,
                });
              }}
              options={TARGET_METRICS.map((m) => ({ value: m.value, label: m.label }))}
              required
            />
            <SelectField
              label="Period"
              value={targetForm.period}
              onChange={(e) => {
                const period = e.target.value;
                const d = periodDates(period);
                setTargetForm({ ...targetForm, period: period as TargetPeriod, effectiveFrom: d.from, effectiveTo: d.to });
              }}
              options={TARGET_PERIODS.map((p) => ({ value: p.value, label: p.label }))}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <InputField
              label={
                targetForm.metric === "revenue"
                  ? "Revenue Target ($)"
                  : `${TARGET_METRICS.find((m) => m.value === targetForm.metric)?.label} Target`
              }
              type="number"
              value={String(targetForm.targetValue)}
              onChange={(e) =>
                setTargetForm({
                  ...targetForm,
                  targetValue: Number(e.target.value),
                  // Kept in step for the revenue path, which older screens and
                  // the bonus tiers still read.
                  revenueTarget: Number(e.target.value),
                })
              }
              required
            />
            <SelectField
              label="Bonus Plan"
              value={targetForm.bonusPlanId}
              onChange={(e) => setTargetForm({ ...targetForm, bonusPlanId: e.target.value })}
              placeholder="No bonus plan"
              options={plans.map((p: any) => ({ value: p._id, label: p.planName }))}
            />
          </div>

          <p className="-mt-1 text-[11px] text-slate-500">
            {TARGET_METRICS.find((m) => m.value === targetForm.metric)?.hint}
          </p>

          {/* Recurrence. Said in full, because "repeats every period" is the
              difference between a target that keeps measuring and one that
              quietly stops at the end of the month. */}
          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={targetForm.recurring}
              onChange={(e) => setTargetForm({ ...targetForm, recurring: e.target.checked })}
              className="mt-0.5 shrink-0"
            />
            <span>
              <span className="block text-[13px] font-semibold text-slate-800">
                Repeat every {targetForm.period === "weekly" ? "week" : "month"}
              </span>
              <span className="block text-[11.5px] text-slate-600 leading-snug">
                The next period opens on its own with the same figure. Each period keeps its own row, so a
                finished one holds the number it was set at and the result it ended with.
              </span>
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <InputField
              label="Effective From"
              type="date"
              value={targetForm.effectiveFrom}
              onChange={(e) => setTargetForm({ ...targetForm, effectiveFrom: e.target.value })}
              required
            />
            <InputField
              label="Effective To"
              type="date"
              value={targetForm.effectiveTo}
              onChange={(e) => setTargetForm({ ...targetForm, effectiveTo: e.target.value })}
              required
            />
          </div>
          <FormRow label="Internal Notes">
            <textarea
              className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] transition-all hover:border-[#2462b2]/50 min-h-[80px] resize-none"
              placeholder="Additional details regarding this benchmark target"
              value={targetForm.notes}
              onChange={(e) => setTargetForm({ ...targetForm, notes: e.target.value })}
            />
          </FormRow>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
            <Button variant="ghost" onClick={resetTargetEditor}>Cancel</Button>
            <Button onClick={handleTargetSubmit} isLoading={savingTarget}>
              <CheckCircle2 size={16} /> {editingTargetId ? "Update Target" : "Save Target Config"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bonus Plan Modal */}
      <Modal isOpen={modalOpen === "plan"} onClose={resetPlanEditor} title={editingPlanId ? "Edit Bonus Plan" : "Construct Bonus Plan"}>
        <div className="space-y-6">
          <InputField
            label="Bonus Plan Name"
            placeholder="e.g. Standard Commission Plan"
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            required
          />
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 pb-1.5">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">Milestone Tiers Ladder</span>
              <button type="button" onClick={addTier} className="text-[11px] font-bold text-[#2462b2] hover:text-[#1e52a0] flex items-center gap-0.5 py-0.5 px-2 rounded hover:bg-blue-50 transition">
                <PlusCircle size={12} /> Add Tier
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto space-y-3 pr-1 cre-scroll">
              {tiers.map((tier, idx) => (
                <div key={idx} className="relative rounded-xl border border-slate-200 bg-slate-50/50 p-3 space-y-2.5 shadow-sm">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-1">
                    <span className="text-[10px] font-extrabold text-[#0b1c30]">Tier #{idx + 1}</span>
                    <button type="button" onClick={() => removeTier(idx)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <MinusCircle size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-[#2462b2]"
                      placeholder="Tier Name"
                      value={tier.tierName}
                      onChange={(e) => setTiers(tiers.map((t, i) => i === idx ? { ...t, tierName: e.target.value } : t))}
                    />
                    <input
                      className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-[#2462b2]"
                      type="number"
                      placeholder="Milestone ($)"
                      value={tier.revenueMilestone || ""}
                      onChange={(e) => setTiers(tiers.map((t, i) => i === idx ? { ...t, revenueMilestone: Number(e.target.value) } : t))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      className="w-full px-2 py-1.5 text-xs border border-slate-300 bg-white rounded-lg focus:outline-none focus:border-[#2462b2]"
                      value={tier.bonusType}
                      onChange={(e) => setTiers(tiers.map((t, i) => i === idx ? { ...t, bonusType: e.target.value as BdBonusTier["bonusType"] } : t))}
                    >
                      <option>Fixed Amount</option>
                      <option>Percentage</option>
                    </select>
                    <input
                      className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-[#2462b2]"
                      type="number"
                      placeholder="Value"
                      value={tier.bonusValue || ""}
                      onChange={(e) => setTiers(tiers.map((t, i) => i === idx ? { ...t, bonusValue: Number(e.target.value) } : t))}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="ghost" onClick={resetPlanEditor} disabled={savingPlan}>Cancel</Button>
            <Button onClick={handlePlanSubmit} isLoading={savingPlan}>
              <BadgePercent size={16} /> {editingPlanId ? "Update Plan" : "Save Plan"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assignment Modal */}
      <Modal isOpen={modalOpen === "assign"} onClose={resetAssignmentEditor} title={editingAssignmentId ? "Edit Target Assignment" : "Assign Target & Plan"}>
        <div className="space-y-4">
          <SelectField
            label="Select Target"
            value={assignmentForm.targetId}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, targetId: e.target.value })}
            placeholder="Select a target"
            options={allTargets
              .filter((t) => t.status === "Active" && !t.isClosed)
              .map((t) => ({
                value: t._id,
                label: `${getName(t.businessDeveloper)} — ${t.targetMonth}/${t.targetYear} — ${money(t.revenueTarget)}`,
              }))}
            required
          />
          <SelectField
            label="Bonus Plan (optional)"
            value={assignmentForm.bonusPlanId}
            onChange={(e) => setAssignmentForm({ ...assignmentForm, bonusPlanId: e.target.value })}
            placeholder="No bonus plan"
            options={plans.map((p) => ({ value: p._id, label: p.planName }))}
          />
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
            <Button variant="ghost" onClick={resetAssignmentEditor}>Cancel</Button>
            <Button onClick={handleAssignmentSubmit} isLoading={savingAssignment}>
              <Users size={16} /> {editingAssignmentId ? "Update Assignment" : "Save Assignment"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assignment Conflict Modal */}
      <Modal isOpen={assignmentConflict.open} onClose={() => setAssignmentConflict({ open: false, assignment: null, message: "" })} title="Assignment Conflict Detected">
        <div className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">{assignmentConflict.message}</p>
          {assignmentConflict.assignment && (
            <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-xs space-y-1.5">
              <p><span className="font-bold text-slate-600">BD:</span> {getName(assignmentConflict.assignment.businessDeveloper)}</p>
              <p><span className="font-bold text-slate-600">Target:</span> {getName(assignmentConflict.assignment.target)} — {(assignmentConflict.assignment.target as any)?.targetMonth}/{(assignmentConflict.assignment.target as any)?.targetYear}</p>
              <p><span className="font-bold text-slate-600">Plan:</span> {assignmentConflict.assignment.bonusPlan ? (assignmentConflict.assignment.bonusPlan as any).planName : "No plan"}</p>
            </div>
          )}
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setAssignmentConflict({ open: false, assignment: null, message: "" })}>Dismiss</Button>
            <Button onClick={() => { if (assignmentConflict.assignment) openAssignmentForEdit(assignmentConflict.assignment); setAssignmentConflict({ open: false, assignment: null, message: "" }); }}>
              Open Existing Assignment
            </Button>
          </div>
        </div>
      </Modal>

    </AdminLayout>
  );
}



